import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { getLobbyConfig } from '../../../../lobby/functions';
import DeviceStatus from '../../../../prejoin/components/web/preview/DeviceStatus';
import { isRoomNameEnabled } from '../../../../prejoin/functions.web';
import Toolbox from '../../../../toolbox/components/web/Toolbox';
import { isButtonEnabled } from '../../../../toolbox/functions.web';
import { getConferenceName } from '../../../conference/functions';
import { PREMEETING_BUTTONS, THIRD_PARTY_PREJOIN_BUTTONS } from '../../../config/constants';
import { isPreCallTestEnabled } from '../../functions';
import ConnectionStatus from './ConnectionStatus';
import Preview from './Preview';
import RecordingWarning from './RecordingWarning';
import UnsafeRoomWarning from './UnsafeRoomWarning';
const useStyles = makeStyles()(theme => {
    return {
        container: {
            height: '100%',
            position: 'absolute',
            inset: '0 0 0 0',
            display: 'flex',
            backgroundColor: theme.palette.ui01,
            zIndex: 252
        },
        content: {
            position: 'fixed',
            bottom: '30px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            boxSizing: 'border-box',
            padding: '5px 10px',
            zIndex: 253,
            width: 'auto',
            minWidth: '600px',
            maxWidth: '800px',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '@media (max-width: 720px)': {
                bottom: '20px',
                minWidth: '280px',
                maxWidth: '90%',
                padding: '6px 12px',
                flexDirection: 'column',
                gap: '12px'
            },
            // mobile phone landscape
            '@media (max-width: 420px)': {
                bottom: '15px',
                minWidth: '260px',
                maxWidth: '95%',
                padding: '4px 8px',
                gap: '8px'
            },
            '@media (max-width: 400px)': {
                minWidth: '240px',
                padding: '3px 6px',
                gap: '6px'
            }
        },
        contentControls: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2px',
            width: '100%',
            '@media (max-width: 720px)': {
                gap: '2px'
            }
        },
        toolsSection: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0px',
            width: '100%',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            paddingTop: '1px',
            marginTop: '0px',
            paddingBottom: '0px'
        },
        paddedContent: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '0px',
            flex: 1,
            justifyContent: 'center',
            padding: '0px',
            '@media (max-width: 720px)': {
                flexDirection: 'column',
                width: '100%',
                gap: '2px',
                padding: '0px'
            },
            '& > *': {
                flex: 'none',
                minWidth: 'auto'
            }
        },
        title: {
            ...theme.typography.heading4,
            color: `${theme.palette.text01}!important`,
            margin: '0',
            textAlign: 'center',
            whiteSpace: 'nowrap',
            fontSize: '1.2rem',
            '@media (max-width: 720px)': {
                fontSize: '1.1rem',
                marginBottom: '4px'
            },
            '@media (max-width: 400px)': {
                fontSize: '1rem',
                display: 'block'
            }
        },
        roomNameContainer: {
            width: 'auto',
            textAlign: 'center',
            margin: '0',
            flexShrink: 0
        },
        roomName: {
            ...theme.typography.heading5,
            color: theme.palette.text01,
            display: 'inline-block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '150px',
            fontSize: '0.9rem',
            '@media (max-width: 720px)': {
                maxWidth: '200px'
            }
        },
        compactCard: {
            // Reset margins typiques qui ajoutent de la hauteur
            '& h1, & h2, & h3, & p': {
                margin: 0
            },
            // ⚠️ Évite "& *" trop agressif, mais OK si tu veux compact
            '& *': {
                lineHeight: 1.15
            },
            // Enlève les marges/paddings qui gonflent dans toolbox/device status
            '& .toolbox, & .toolbox-content, & .toolbox-buttons': {
                margin: 0,
                padding: 0
            },
            // Très souvent le device status ajoute un margin
            '& [data-testid="device-status"], & .device-status': {
                margin: 0,
                padding: 0
            },
            // Boutons (si un margin par défaut existe)
            '& button': {
                margin: 0
            }
        },
    };
});
const PreMeetingScreen = ({ _buttons, _isPreCallTestEnabled, _premeetingBackground, _roomName, children, className, showDeviceStatus, showRecordingWarning, showUnsafeRoomWarning, skipPrejoinButton, title, videoMuted, videoTrack }) => {
    const { classes } = useStyles();
    const style = _premeetingBackground ? {
        background: _premeetingBackground,
        backgroundPosition: 'center',
        backgroundSize: 'cover'
    } : {};
    const roomNameRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    useEffect(() => {
        if (roomNameRef.current) {
            const element = roomNameRef.current;
            const elementStyles = window.getComputedStyle(element);
            const elementWidth = Math.floor(parseFloat(elementStyles.width));
            setIsOverflowing(element.scrollWidth > elementWidth + 1);
        }
    }, [_roomName]);
    return (React.createElement("div", { className: clsx('premeeting-screen', classes.container, className) },
        React.createElement("div", { style: style },
            React.createElement("div", { className: clsx(classes.content, classes.compactCard) },
                _isPreCallTestEnabled && React.createElement(ConnectionStatus, null),
                React.createElement("div", { className: classes.contentControls },
                    React.createElement("div", { className: classes.paddedContent }, children),
                    React.createElement("div", { className: classes.toolsSection },
                        _buttons.length && React.createElement(Toolbox, { toolbarButtons: _buttons }),
                        React.createElement("div", { className: classes.paddedContent },
                            skipPrejoinButton,
                            showUnsafeRoomWarning && React.createElement(UnsafeRoomWarning, null),
                            showDeviceStatus && React.createElement(DeviceStatus, null),
                            showRecordingWarning && React.createElement(RecordingWarning, null)))))),
        React.createElement(Preview, { videoMuted: videoMuted, videoTrack: videoTrack })));
};
/**
 * Maps (parts of) the redux state to the React {@code Component} props.
 *
 * @param {Object} state - The redux state.
 * @param {Object} ownProps - The props passed to the component.
 * @returns {Object}
 */
function mapStateToProps(state, ownProps) {
    const { hiddenPremeetingButtons, prejoinConfig } = state['features/base/config'];
    const { toolbarButtons } = state['features/toolbox'];
    const { knocking } = state['features/lobby'];
    const { showHangUp: showHangUpLobby = true } = getLobbyConfig(state);
    const { showHangUp: showHangUpPrejoin = true } = prejoinConfig || {};
    const premeetingButtons = (ownProps.thirdParty
        ? THIRD_PARTY_PREJOIN_BUTTONS
        : PREMEETING_BUTTONS).filter((b) => !(hiddenPremeetingButtons || []).includes(b));
    const shouldShowHangUp = knocking ? showHangUpLobby : false;
    if (shouldShowHangUp && !premeetingButtons.includes('hangup')) {
        premeetingButtons.push('hangup');
    }
    const { premeetingBackground } = state['features/dynamic-branding'];
    return {
        // For keeping backwards compat.: if we pass an empty hiddenPremeetingButtons
        // array through external api, we have all prejoin buttons present on premeeting
        // screen regardless of passed values into toolbarButtons config overwrite.
        // If hiddenPremeetingButtons is missing, we hide the buttons according to
        // toolbarButtons config overwrite.
        _buttons: hiddenPremeetingButtons
            ? premeetingButtons
            : premeetingButtons.filter(b => isButtonEnabled(b, toolbarButtons)),
        _isPreCallTestEnabled: isPreCallTestEnabled(state),
        _premeetingBackground: premeetingBackground,
        _roomName: isRoomNameEnabled(state) ? getConferenceName(state) : ''
    };
}
export default connect(mapStateToProps)(PreMeetingScreen);
